import { useCallback } from 'react'
import styled, { css } from 'styled-components'
import BodyText, { BodyTextMedium } from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import noop from 'lib/function/noop'
import { rem } from 'polished'

interface TourOfferCampaignBannerProps {
  className?: string
  eyebrow: string
  title: string
  description: string
  termsLabel?: string
  termsHref?: string
  onTermsClick?: () => void
}

const BannerStage = styled.section`
  position: relative;
  display: flex;
  justify-content: center;
  background-color: rgba(172, 234, 227, 0.16);
  padding: ${rem(20)} 0;

  @media (min-width: 768px) {
    justify-content: flex-end;
  }
`

const BannerCard = styled.div`
  position: relative;
  margin: 0;
  max-width: ${rem(780)};
  overflow: hidden;
  border: 1px solid rgba(172, 234, 227, 0.72);
  border-radius: ${rem(8)};
  background: linear-gradient(180deg, #5DB5AA, #003d37 82%);
  box-shadow: 0 ${rem(14)} ${rem(30)} rgba(0, 61, 55, 0.16);
  padding: ${rem(22)} ${rem(20)};

  @media (min-width: 768px) {
    padding: ${rem(24)} ${rem(28)};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: ${rem(6)};
    height: 100%;
    background-color: #ACEAE3;
  }
`

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: ${rem(3)} ${rem(10)};
  background-color: #ACEAE3;
  color: ${props => props.theme.palette.neutral.default.one};
  font-size: ${rem(12)};
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (min-width: 768px) {
    align-items: flex-start;
    text-align: left;
  }
`

const BannerHeading = styled(Heading)`
  margin-top: ${rem(10)};
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
`

const BannerDescription = styled(BodyText)`
  margin: ${rem(8)} auto 0;
  max-width: ${rem(520)};
  color: ${props => props.theme.palette.neutral.surface.staticWhite};

  @media (min-width: 768px) {
    margin-right: 0;
    margin-left: 0;
  }
`

const TermsAnchorStyles = css`
  ${BodyTextMedium}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: ${rem(12)};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
  font-weight: 600;
  line-height: 1.2;
  text-decoration: underline;
  text-underline-offset: ${rem(3)};
  cursor: pointer;
`

const TermsAnchor = styled.a`
  ${TermsAnchorStyles}
`

const TermsButton = styled.button`
  ${TermsAnchorStyles}
`

function TourOfferCampaignBanner(props: TourOfferCampaignBannerProps) {
  const {
    className,
    eyebrow,
    title,
    description,
    termsLabel,
    termsHref,
    onTermsClick = noop,
  } = props

  const handleTermsClick = useCallback(() => {
    onTermsClick()
  }, [onTermsClick])
  let termsAction = null

  if (termsLabel) {
    if (termsHref) {
      termsAction = <TermsAnchor href={termsHref} onClick={handleTermsClick}>
        {termsLabel}
      </TermsAnchor>
    } else {
      termsAction = <TermsButton type="button" onClick={handleTermsClick}>
        {termsLabel}
      </TermsButton>
    }
  }

  return (
    <BannerStage className={className}>
      <BannerCard>
        <CardContent>
          <Eyebrow>{eyebrow}</Eyebrow>
          <BannerHeading variant="heading4">
            {title}
          </BannerHeading>
          <BannerDescription variant="medium">
            {description}
          </BannerDescription>
          {termsAction}
        </CardContent>
      </BannerCard>
    </BannerStage>
  )
}

export type { TourOfferCampaignBannerProps }
export default TourOfferCampaignBanner
